import Typography from "@portal/components/atoms/v2/typography";
import { useState } from "react";
import { useRobot } from "@portal/app/context/robotProvider";
import { BehaviourChart } from "./helpers/BehaviourChart";
import { MapBehaviourToIndex, MapIndexToBehaviour } from "./helpers/MapData";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { SelectionBoxComponent } from "../components/selectionBox/SelectionBoxComponent";
import { RobotFooterComponent, RobotNavigateToPage } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { ToggleButton } from "@portal/components/atoms/buttons/toggle/ToggleButton";
import { UserMessageComponent } from "../components/message/UserMessageComponent";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { RiskAppetite } from "@portal/types/robot/IRobotEnums";
import { MainBox } from "../components/mainBox";
import { useSession } from "@portal/auth/client/SessionProvider";

export const BehaviourPage = ({ pageIndex, centerId, isDefaultLanguage }: IPageProps) => {
  const router = useRouter();
  const { riskLevelRequest: request, setBehaviour } = useRobot();
  const [isToggleBoxOpen, setIsToggleBoxOpen] = useState(false);
  const isButtonDisabled = request.behaviour === null;
  const [showUserMessage, setShowUserMessage] = useState(false);
  const { session } = useSession();
  const isLoggedIn = session?.status === "authenticated";

  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const options = [texts.PAGE_6_A, texts.PAGE_6_B, texts.PAGE_6_C];

  const OnSelectionClick = (selectedOption: number) => {
    if (request.riskAppetite === RiskAppetite.Offensive && selectedOption === 0) {
      setShowUserMessage(true);
    }

    setBehaviour(MapIndexToBehaviour(selectedOption));
  };

  return (
    <>
      <MainBox>
        <Typography variant="Body">{texts.PAGE_6_INFO_HEADER}</Typography>
        <div className="flex mt-2.5">
          <Typography variant="H6">{texts.PAGE_6_SUBHEADER_2}</Typography>
        </div>
        <TextLine />
        <SelectionBoxComponent
          options={options}
          selectedOption={MapBehaviourToIndex(request.behaviour) ?? -1}
          onSelectionClick={OnSelectionClick}
        />
        <ToggleButton
          activeText={texts.PAGE_6_BUTTON_TEXT}
          inActiveText={texts.TEXT_CLOSE}
          expanded={isToggleBoxOpen}
          setExpanded={setIsToggleBoxOpen}
        />
        {isToggleBoxOpen && (
          <div>
            <div className="flex flex-col my-3 border dark:border-blueBorderDark rounded-md w-full p-1 bg-pageBoxBackgroundLight dark:bg-pageBoxBackgroundDark">
              <BehaviourChart />
            </div>
            <div className="flex mt-1 p-2">
              <Typography variant="Body">{texts.PAGE_6_INFO_TEXT}</Typography>
            </div>
          </div>
        )}
      </MainBox>
      {showUserMessage && (
        <UserMessageComponent
          content={
            <div>
              <Typography variant="BodySpan">{texts.MESSAGE_WARNING_BEHAVIOUR}</Typography>
              <Link href={`/robot/3?centerId=${centerId}`} className="text-blueHighlighted">
                her
              </Link>
            </div>
          }
          showDialog={showUserMessage}
          primaryButtonText={texts.SHORT_HORIZON_CONTINUE}
          primaryButtonClick={() => {
            setShowUserMessage(false);
          }}
          secondaryButtonText={texts.SHORT_HORIZON_GO_BACK}
          secondaryButtonClick={() => {
            setBehaviour(null);
            setShowUserMessage(false);
            router.push(RobotNavigateToPage(isLoggedIn, 5, centerId));
          }}
        />
      )}
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isButtonDisabled}
      />
    </>
  );
};
